import { OSKGeoJson } from '../models';
import { Configuration } from './generated';
import globalAxios, { AxiosInstance } from 'axios';
import { CQL2Expr } from './cql2';
import { ProxyAPI, ProxyApiType } from './proxy';

export const DEFAULT_STAC_COLLECTION = 'ghost-hsi-l1a';

export const MAX_STAC_RESULTS = 200;

/** Represents available parameters when performing a search */
export type StacSearchItemRequest = {
    /** The stac collection(s) to search. If none are provided, the default is used. */
    collections?: string[];

    /** The earliest date that results can be from.
     * If empty, no limit. */
    capturedAfter?: Date;

    /** The latest date that results can be from.
     * If empty, no limit. */
    capturedBefore?: Date;

    /** A list of sensor ids.
     * Filterd against the `osk:sensor_serial` property. */
    sensor?: number[];

    /** Maximum cloud cover value for results, from 0-100.
     * Filtered against the `eo:cloud_cover` property.*/
    cloudCoverMax?: number;

    /** The bounding area to search inside. Sets the `bbox` filter. */
    aoi?: OSKGeoJson;

    /** A list of task ids to filter by.
     * Filtered against the `osk:task_id` property.
     */
    taskIds?: string[];

    /** A list of item ids to filter by. */
    ids?: string[];

    /** The target range (`[low, high]`) of values for sun azimuth angles. */
    sunAzimuthRange?: number[];

    /** The target range (`[low, high]`) of values for sun elevation angles. */
    sunElevationRange?: number[];

    /** The target range (`[low, high]`) of values for off nadir angles. */
    offNadirRange?: number[];

    /** The target range (`[low, high]`) of values for view azimuth angles. */
    viewAzimuthRange?: number[];

    /** The imaging modes to search for. `Narrow, Wide, Linescan, Unknown` */
    imagingModes?: string[];

    /** Whether to only return items that have been purchased for the current program. */
    allowedPrograms?: number;

    /** A list of tasking order IDs to filter by */
    taskingOrderIds?: string[];
};

/** Represents a STAC Collection object */
export type StacCollection = {
    /** The collection's unique identifier, not display-friendly */
    id: string;

    /** Keywords associated with the collection */
    keywords: string[];

    /** The collection's display-friendly title */
    title: string;

    /** The collection's display-friendly description */
    description: string;
};

/**
 * StacApi - object-oriented interface
 * @export
 * @class StacApi
 * @extends {ProxyAPI}
 */
export class StacApi extends ProxyAPI {
    constructor(configuration?: Configuration, protected axios: AxiosInstance = globalAxios) {
        super(ProxyApiType.STAC, configuration, axios);
    }

    /** A method that returns all collections available to this user.
     * It's automatically filtered to only return collections that the user
     * has access to.
     */
    public getCollections() {
        return this.axios
            .get(`${this.basePath}/collections`, {})
            .then((response) => response.data.collections as StacCollection[]);
    }

    public getItemsForCollection(collection: string) {
        return this.axios.get(`${this.basePath}/collections/${collection}/items`, {}).then((response) => response.data);
    }

    public async searchCatalog(options: StacSearchItemRequest) {
        // TODO: This needs to be retrieved from the /collections endpoint
        const collections: string[] = options.collections ?? [DEFAULT_STAC_COLLECTION];
        const DATETIME_FORMAT = 'aa/bb';
        const capturedAfterISO = options.capturedAfter?.toISOString() ?? '..';
        const capturedBeforeISO = options.capturedBefore?.toISOString() ?? '..';
        const datetime = DATETIME_FORMAT.replace('aa', capturedAfterISO).replace('bb', capturedBeforeISO);

        const searchExpr = new CQL2Expr();
        const cloudExpr = new CQL2Expr('or');
        if (options.cloudCoverMax) {
            cloudExpr.addClause('eo:cloud_cover', 'between', 0, options.cloudCoverMax);
            cloudExpr.addClause('eo:cloud_cover', 'isNull');
            searchExpr.addExpr(cloudExpr);
        }
        if (options.sensor) searchExpr.addClause('osk:sensor_serial', 'in', options.sensor);
        if (options.taskIds) searchExpr.addClause('osk:task_id', 'in', options.taskIds);
        if (options.sunAzimuthRange)
            searchExpr.addClause('view:sun_azimuth', 'between', options.sunAzimuthRange[0], options.sunAzimuthRange[1]);
        if (options.sunElevationRange)
            searchExpr.addClause(
                'view:sun_elevation',
                'between',
                options.sunElevationRange[0],
                options.sunElevationRange[1],
            );
        if (options.offNadirRange)
            searchExpr.addClause('view:off_nadir', 'between', options.offNadirRange[0], options.offNadirRange[1]);
        if (options.viewAzimuthRange)
            searchExpr.addClause('view:azimuth', 'between', options.viewAzimuthRange[0], options.viewAzimuthRange[1]);
        if (options.imagingModes) searchExpr.addClause('osk:imaging_mode', 'in', options.imagingModes);
        if (options.allowedPrograms)
            searchExpr.addClause('osk:access_control.allowed_programs', '=', options.allowedPrograms);
        if (options.taskingOrderIds) searchExpr.addClause('osk:access_control.orders', 'in', options.taskingOrderIds);

        const params = {
            limit: MAX_STAC_RESULTS,
            collections,
            ...((options.capturedAfter || options.capturedBefore) && { datetime }),
            ...(options.aoi && { intersects: options.aoi?.features[0] }),
            ...(searchExpr.hasFilter() && { filter: searchExpr.json() }),
            ...(options.ids && { ids: options.ids }),
        };

        return this.axios
            .post(
                `${this.basePath}/search`,
                {
                    ...params,
                },
                { headers: { 'content-type': 'application/json' } },
            )
            .then((response) => response.data);
    }
}
